import React, { useMemo, useState } from "react";
import { Loader2, FileSpreadsheet, Printer } from "lucide-react";
import {
  useGetDuesAnalysisQuery,
  useGetChaptersQuery,
  useGetStatesQuery,
  useGetUserCategoriesQuery,
  useGetMembershipBandsQuery,
  useLazyGetDuesAnalysisPaymentsQuery,
} from "../api/billingApi";
import {
  exportFinanceAnalysisCsv,
  exportFinanceAnalysisPdf,
} from "../utils/exportFinanceAnalysis";

/**
 * Invoice amounts are stored in KOBO — the API says so in `summary.unit` and
 * returns the rows untouched. Every figure on this page therefore goes through
 * here. It used to print the raw value straight out, so every amount read a
 * hundred times too high, and looked entirely believable while doing it.
 */
const naira = (kobo: any) =>
  `₦${(Number(kobo || 0) / 100).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Every way the Institute takes money in.
 *
 * `member_dues` and `development_levy` are classified by the API from the
 * invoice LINE, not from `invoices.source`: the levy is billed on the same
 * invoice as the subscription, and dues proper means everything else on a dues
 * invoice — annual subscription, migrated legacy dues, onboarding batch import.
 */
const SOURCE_LABELS: Record<string, string> = {
  member_dues: "Member dues",
  development_levy: "Development levy",
  event_fee: "Events",
  exam_fee: "Exams",
  application_fee: "New applications",
  upgrade_fee: "Upgrade applications",
  upgrade_induction: "Induction",
  logbook_assessment: "Logbook assessment",
  firm_registration_fee: "Firm registration",
};

const SOURCE_ORDER = Object.keys(SOURCE_LABELS);

export default function FinanceAnalysisPage() {
  const [filters, setFilters] = useState({
    year: "",
    // A day-level window, narrowing whatever the year leaves. Either end stands
    // alone: a start with no end means "from that day onwards".
    start_date: "",
    end_date: "",
    chapter_id: "",
    state_id: "",
    category_id: "",
    band_id: "",
    source: "",
    dues_only: false,
  });

  // No `with_rows` — this screen reports totals by source, not a list of
  // members and what each of them owes.
  const { data, isLoading, isFetching } = useGetDuesAnalysisQuery(filters);

  const { data: chapters = [] } = useGetChaptersQuery();
  const { data: states = [] } = useGetStatesQuery();
  const { data: categories = [] } = useGetUserCategoriesQuery();
  const { data: bands = [] } = useGetMembershipBandsQuery();

  const years = [2026, 2025, 2024, 2023, 2022];

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const dateRangeOn = !!filters.start_date || !!filters.end_date;

  /* A range entered back to front returns nothing, and an empty report with no
     explanation reads as "there was no income" rather than "those dates are the
     wrong way round". */
  const datesReversed =
    !!filters.start_date &&
    !!filters.end_date &&
    filters.start_date > filters.end_date;

  const summary = data?.summary;
  const bySource: any[] = data?.by_source ?? [];
  const breakdown = data?.breakdown ?? null;
  // Only dues and the levy can be OWED. Every other source is a voluntary fee
  // raised when somebody starts something they may never finish, so an unpaid
  // one is an abandoned intention rather than a debt, and "expected" and
  // "outstanding" are not figures anyone should be reading.
  const receivedOnly = !!data?.received_only;
  const isEvents = breakdown?.source === "event_fee";

  /**
   * Receipts, not positions.
   *
   * With a day range on, the server reports what came IN during the window and
   * returns null for expected and outstanding — those are true as at a moment,
   * and a window has no moment. So those columns come off the table entirely
   * rather than printing a null as ₦0.00, which would read as "nothing billed".
   */
  const receipts = summary?.basis === "receipts";

  // The catalogue above, plus anything the API reports that it does not know
  // about — a source added on the server should not be unselectable here.
  const sourceOptions = useMemo(() => {
    const seen = new Set(SOURCE_ORDER);
    const extra = bySource
      .map((r) => String(r.source))
      .filter((s) => s && !seen.has(s));
    return [...SOURCE_ORDER, ...Array.from(new Set(extra))];
  }, [bySource]);

  /**
   * The filters in force, in words.
   *
   * Resolved to NAMES, not ids: "Chapter: Lagos" is a statement somebody can
   * check a week later, "chapter_id: 7" is not. Only the ones actually set —
   * a header listing six "All" rows says nothing.
   */
  const activeFilters = useMemo(() => {
    const named = (list: any[], id: string, key = "name") =>
      list?.find((x: any) => String(x.id) === String(id))?.[key] ?? id;

    const out: Record<string, string> = {};

    if (filters.year) out["Year"] = filters.year;
    if (filters.start_date || filters.end_date) {
      out["Days"] =
        `${filters.start_date || "from the beginning"} to ${filters.end_date || "today"}`;
    }
    if (filters.source) out["Source"] = SOURCE_LABELS[filters.source] ?? filters.source;
    if (filters.chapter_id) out["Chapter"] = named(chapters, filters.chapter_id);
    if (filters.state_id) out["State"] = named(states, filters.state_id);
    if (filters.category_id) out["Category"] = named(categories?.data ?? [], filters.category_id);
    if (filters.band_id) out["Band"] = named(bands, filters.band_id, "label");
    if (filters.dues_only) out["Annual dues only"] = "Yes";

    return out;
  }, [filters, chapters, states, categories, bands]);

  /**
   * The transaction list is fetched only when somebody exports.
   *
   * It runs to thousands of rows and is not cached server-side, so every
   * ordinary view of this page must not pay for it. The aggregates in the sheet
   * still come from what is already on screen — see the exporter's note.
   */
  const [fetchPayments, { isFetching: fetchingPayments }] =
    useLazyGetDuesAnalysisPaymentsQuery();

  const [exportError, setExportError] = useState("");

  const exportReport = async (as: "csv" | "pdf") => {
    setExportError("");

    try {
      const withPayments: any = await fetchPayments(filters).unwrap();

      const payload = {
        summary,
        bySource,
        byStatus: (data?.by_status ?? []) as any[],
        breakdown,
        sourceLabels: SOURCE_LABELS,
        meta: {
          filters: activeFilters,
          receivedOnly,
          sourceLabel: filters.source
            ? SOURCE_LABELS[filters.source] ?? filters.source
            : null,
        },
        payments: withPayments?.payments ?? [],
        paymentsTruncated: !!withPayments?.payments_truncated,
        paymentsTotal: withPayments?.payments_total ?? null,
      };

      if (as === "csv") exportFinanceAnalysisCsv(payload);
      else exportFinanceAnalysisPdf(payload);
    } catch (e: any) {
      setExportError(
        e?.data?.message ??
          "The payment list could not be fetched, so the export was not produced. Try a narrower filter.",
      );
    }
  };

  // Nothing resolved under the filters — an export of an empty report is a
  // sheet somebody will mistake for a period with no income.
  const nothingToExport = !data || bySource.length === 0;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Finance Analysis</h1>
          <p className="text-sm text-gray-500 mt-1 mb-4 max-w-3xl">
            Where the Institute's money came from. Received counts successful
            payments only — a pending, failed or abandoned attempt is not income.
            Expected and outstanding are shown for dues and the development levy
            alone: every other source is a voluntary fee, so an unpaid one is an
            abandoned intention rather than a debt.
          </p>
        </div>

        {/* Takes the report AS FILTERED, and both sheets name the filters that
            produced it — a finance total with nothing saying what it covers is
            the one thing nobody can check later. */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportReport("csv")}
              disabled={nothingToExport || fetchingPayments}
              title={
                nothingToExport
                  ? "Nothing to export under these filters"
                  : "Download this report, as filtered, with every payment behind it"
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {fetchingPayments ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Excel
            </button>
            <button
              onClick={() => exportReport("pdf")}
              disabled={nothingToExport || fetchingPayments}
              title={
                nothingToExport
                  ? "Nothing to export under these filters"
                  : "Print this report, as filtered, with every payment behind it"
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {fetchingPayments ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Printer className="w-4 h-4" />
              )}
              PDF
            </button>
          </div>

          {/* The payment list is fetched on press, so a wide filter takes a
              moment — said rather than leaving the button looking stuck. */}
          {fetchingPayments && (
            <span className="text-[11px] text-gray-500">Fetching the payment list…</span>
          )}
          {exportError && (
            <span className="text-[11px] font-semibold text-rose-600 max-w-xs text-right">
              {exportError}
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 mb-6 space-y-4 shadow">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <select name="year" value={filters.year} onChange={handleChange} className="border rounded p-2">
            <option value="">All Years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <select name="source" value={filters.source} onChange={handleChange} className="border rounded p-2">
            <option value="">All Sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>{SOURCE_LABELS[s] ?? s}</option>
            ))}
          </select>

          <select name="chapter_id" value={filters.chapter_id} onChange={handleChange} className="border rounded p-2">
            <option value="">All Chapters</option>
            {chapters?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select name="state_id" value={filters.state_id} onChange={handleChange} className="border rounded p-2">
            <option value="">All States</option>
            {states?.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select name="category_id" value={filters.category_id} onChange={handleChange} className="border rounded p-2">
            <option value="">All Categories</option>
            {categories?.data?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select name="band_id" value={filters.band_id} onChange={handleChange} className="border rounded p-2">
            <option value="">All Bands</option>
            {bands?.map((b: any) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </div>

        {/* ── Day range ────────────────────────────────────────────────────
            Its own labelled row rather than two more boxes among the six
            selects: the pair is one filter, and an unlabelled date input beside
            "All Bands" gives no clue which date it means. */}
        <div className="border-t pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                From day
              </label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                max={filters.end_date || undefined}
                onChange={handleChange}
                className="border rounded p-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                To day
              </label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                min={filters.start_date || undefined}
                onChange={handleChange}
                className="border rounded p-2 text-sm"
              />
            </div>

            {dateRangeOn && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, start_date: "", end_date: "" })}
                className="text-xs font-bold text-gray-400 hover:text-gray-700 pb-2"
              >
                Clear dates
              </button>
            )}

            <p className="text-[11px] text-gray-500 max-w-md pb-1">
              {dateRangeOn ? (
                <>
                  Counting money <b>paid</b> between{" "}
                  {filters.start_date ? <b>{filters.start_date}</b> : "the beginning"} and{" "}
                  {filters.end_date ? <b>{filters.end_date}</b> : "today"}. Expected and
                  outstanding are not reported for a date range — they are positions, not a flow.
                </>
              ) : (
                <>
                  Leave both blank for the whole period. Set either end and the page reports what
                  was <b>received</b> in that window, by the date each payment was made.
                </>
              )}
            </p>
          </div>

          {datesReversed && (
            <p className="mt-2 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              "From day" is after "to day", so this window contains nothing. Swap them.
            </p>
          )}

          {/* The year and the range ask different questions and compose: the
              year says which dues the money was FOR, the range says when it
              arrived. Spelt out, because "2026 + November" reads as a
              contradiction until you see which half is which. */}
          {dateRangeOn && !!filters.year && (
            <p className="mt-2 text-[11px] text-gray-500">
              {filters.year} dues and fees, paid during these days — the year is what the money was
              for, the days are when it came in.
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.dues_only}
            onChange={(e) => setFilters({ ...filters, dues_only: e.target.checked })}
            className="w-4 h-4 accent-indigo-600"
          />
          Annual dues only — leave out exam, event, upgrade and firm fees
          <span className="text-gray-400">(voluntary, so an unpaid one is not arrears)</span>
        </label>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <div className={`space-y-6 ${isFetching ? "opacity-60" : ""}`}>
          {/* Summary */}
          <div className={`grid grid-cols-2 gap-4 ${receivedOnly ? "md:grid-cols-2" : "md:grid-cols-4"}`}>
            {/* Expected, outstanding and the collection rate are all statements
                about a debt. An unpaid event registration is not one, so for
                events the page shows what came in and nothing else. */}
            {!receivedOnly && (
              <div className="bg-gray-900 text-white p-4 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Total Expected</p>
                <p className="text-xl font-black mt-1">
                  {naira(summary?.total_expected ?? summary?.total_due)}
                </p>
                <p className="text-[10px] opacity-60 mt-1">
                  Dues and levy only ·{" "}
                  {Number(summary?.invoices || 0).toLocaleString()} invoices
                </p>
              </div>
            )}
            <div className="bg-green-50 border border-green-200 text-green-900 p-4 rounded-xl">
              <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Received</p>
              <p className="text-xl font-black mt-1">{naira(summary?.total_paid)}</p>
              <p className="text-[10px] opacity-60 mt-1">Successful payments only</p>
            </div>
            {!receivedOnly && (
              <div className="bg-red-50 border border-red-200 text-red-900 p-4 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Outstanding</p>
                <p className="text-xl font-black mt-1">{naira(summary?.total_balance)}</p>
              </div>
            )}
            {receivedOnly ? (
              <div className="bg-indigo-50 border border-indigo-200 text-indigo-900 p-4 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">
                  {breakdown ? `${breakdown.dimension}s` : "Invoices"}
                </p>
                <p className="text-xl font-black mt-1">
                  {Number(
                    breakdown ? breakdown.rows?.length ?? 0 : summary?.invoices ?? 0,
                  ).toLocaleString()}
                </p>
                <p className="text-[10px] opacity-60 mt-1">that took money</p>
              </div>
            ) : (
              <div className="bg-indigo-50 border border-indigo-200 text-indigo-900 p-4 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Collection Rate</p>
                <p className="text-xl font-black mt-1">{summary?.collection_rate ?? 0}%</p>
              </div>
            )}
          </div>

          {/* By source */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <h2 className="font-bold px-4 py-3 border-b bg-gray-50 text-sm">
              {receipts ? "Money received, by source" : "Income by Source"}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2">Source</th>
                    {receipts && <th className="px-4 py-2 text-right">Payments</th>}
                    <th className="px-4 py-2 text-right">Invoices</th>
                    {!receipts && <th className="px-4 py-2 text-right">Expected</th>}
                    <th className="px-4 py-2 text-right">Received</th>
                    {!receipts && <th className="px-4 py-2 text-right">Outstanding</th>}
                  </tr>
                </thead>
                <tbody>
                  {bySource.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">
                        {receipts
                          ? "No money came in under these filters."
                          : "Nothing billed under these filters."}
                      </td>
                    </tr>
                  ) : bySource.map((r: any) => (
                    <tr key={String(r.source)} className="border-t even:bg-gray-50/50">
                      <td className="px-4 py-2 font-semibold">
                        {SOURCE_LABELS[r.source] ?? r.source}
                        {!r.is_dues && (
                          <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            not dues
                          </span>
                        )}
                      </td>
                      {receipts && (
                        <td className="px-4 py-2 text-right tabular-nums">
                          {Number(r.payments || 0).toLocaleString()}
                        </td>
                      )}
                      <td className="px-4 py-2 text-right tabular-nums">{Number(r.count || 0).toLocaleString()}</td>
                      {/* Only dues and the levy can be owed, so only they get
                          an expected and an outstanding figure. The rest are
                          voluntary fees — a dash, not a zero, which would read
                          as "nothing outstanding" rather than "not applicable". */}
                      {!receipts && (
                        <td className="px-4 py-2 text-right tabular-nums">
                          {r.is_dues ? naira(r.due) : <span className="text-gray-300">—</span>}
                        </td>
                      )}
                      <td className="px-4 py-2 text-right tabular-nums text-green-700">{naira(r.paid)}</td>
                      {!receipts && (
                        <td className={`px-4 py-2 text-right tabular-nums ${r.is_dues && r.balance > 0 ? "text-red-600" : "text-gray-400"}`}>
                          {r.is_dues ? naira(r.balance) : <span className="text-gray-300">—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Where the selected source's money came from */}
          {breakdown && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-baseline justify-between gap-3">
                <h2 className="font-bold text-sm">
                  {SOURCE_LABELS[breakdown.source] ?? breakdown.source} by{" "}
                  {String(breakdown.dimension).toLowerCase()}
                </h2>
                {receivedOnly && (
                  <span className="text-[11px] text-gray-500">
                    {isEvents
                      ? "Successful payments only — guests and registrants both counted"
                      : "Successful payments only"}
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2">{breakdown.dimension}</th>
                      {/* Registrant/guest is an event idea — only the event
                          breakdown carries those counts. */}
                      {isEvents ? (
                        <>
                          <th className="px-4 py-2 text-right">Registrants</th>
                          <th className="px-4 py-2 text-right">Guests</th>
                        </>
                      ) : (
                        <th className="px-4 py-2 text-right">Invoices</th>
                      )}
                      {!receivedOnly && <th className="px-4 py-2 text-right">Expected</th>}
                      <th className="px-4 py-2 text-right">Received</th>
                      {!receivedOnly && <th className="px-4 py-2 text-right">Outstanding</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.rows?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">
                          Nothing recorded under these filters.
                        </td>
                      </tr>
                    ) : breakdown.rows?.map((r: any, i: number) => (
                      <tr key={`${r.label}-${i}`} className="border-t even:bg-gray-50/50">
                        <td className="px-4 py-2 font-semibold">{r.label}</td>
                        {isEvents ? (
                          <>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {Number(r.registrants || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {Number(r.guests || 0).toLocaleString()}
                            </td>
                          </>
                        ) : (
                          <td className="px-4 py-2 text-right tabular-nums">
                            {Number(r.count || 0).toLocaleString()}
                          </td>
                        )}
                        {!receivedOnly && (
                          <td className="px-4 py-2 text-right tabular-nums">{naira(r.due)}</td>
                        )}
                        <td className="px-4 py-2 text-right tabular-nums text-green-700 font-semibold">
                          {naira(r.paid)}
                        </td>
                        {!receivedOnly && (
                          <td className={`px-4 py-2 text-right tabular-nums ${r.balance > 0 ? "text-red-600" : "text-gray-400"}`}>
                            {naira(r.balance)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {breakdown.truncated && (
                <p className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200">
                  Showing the top 200 only — narrow the filters to see the rest.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
